import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn, formatPercent } from '@/lib/utils';
import {
  ArrowLeft,
  Loader2,
  AlertTriangle,
  CalendarClock,
  CalendarRange,
  FastForward,
  FileText,
  History,
  Landmark,
  Lock,
  RefreshCw,
  RotateCcw,
  School,
  Settings2,
  Users,
  CopyX,
  UserPlus,
  MapPin,
  X,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { migrationV1V2Api, type BirthPlaceBackfillResult } from './api/migration-v1-v2';
import type { SchedulesV2Status } from './api/migration-v1-v2';
import type { CollapsedParentRepairReport, DuplicateUserRepairReport, V1BillingLockdownReport } from './types';
import { ProjectStatusBadge } from './components/ProjectStatusBadge';
import { ProjectConfigDialog } from './components/ProjectConfigDialog';
import { V2TargetStatusCard } from './components/V2TargetStatusCard';
import { LotsTable } from './components/LotsTable';
import type { CategoryAction } from './components/LotsTable';
import { isStepComingSoon, isStepOutOfPerimeter, V1_BILLING_LOCKDOWN_ENABLED } from './components/modulePerimeter';
import { effectiveBucketOfStep, isLotEmpty, unprocessedRecords } from './components/lotStatus';
import type { LotStatusBucket } from './components/lotStatus';
import { RoleMappingsDialog } from './components/RoleMappingsDialog';
import { LotMappingDialog } from './components/LotMappingDialog';
import { CustomFieldsMappingDialog } from './components/CustomFieldsMappingDialog';
import { LOT_MAPPING_REGISTRY, LOTS_WITH_MAPPING } from './components/lotMappingRegistry';
import { AssignUserControl } from './components/AssignUserControl';
import { PlannedDateControl } from './components/PlannedDateControl';
import { ProjectComments } from './components/ProjectComments';
import { useAssignableUsers } from './hooks/useAssignableUsers';
import type { MigrationProject, MigrationStepExecution } from './types';

type FilterBucket = 'all' | LotStatusBucket;

const FILTER_DEFS: { key: FilterBucket; label: string }[] = [
  { key: 'all', label: 'Tous' },
  { key: 'pending', label: 'À migrer' },
  { key: 'running', label: 'En cours' },
  { key: 'completed', label: 'Migrés' },
  { key: 'review', label: 'À traiter' },
];

interface HeaderStatProps {
  label: string;
  value: number;
  tone?: 'default' | 'success' | 'violet' | 'orange' | 'red';
}

const TONE_CLASS: Record<NonNullable<HeaderStatProps['tone']>, string> = {
  default: 'text-gray-900 dark:text-gray-100',
  success: 'text-emerald-600',
  violet: 'text-violet-600',
  orange: 'text-orange-600',
  red: 'text-red-600',
};

function HeaderStat({ label, value, tone = 'default' }: HeaderStatProps) {
  return (
    <div className="px-4 py-2.5">
      <p className="text-xs font-medium tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
      <p className={cn('text-2xl font-bold', TONE_CLASS[tone])}>{value}</p>
    </div>
  );
}

/**
 * Libellés FR des types d'école (cf. backend `Constants::SCHOOL_TYPE`). La
 * valeur V1 arrive en majuscules (`PRIVATE_OUT_OF_CONTRACT`), on normalise
 * avant lookup. Repli : version « humanisée » du code pour un type inconnu.
 */
const SCHOOL_TYPE_LABELS: Record<string, string> = {
  private_out_of_contract: 'Privée hors contrat',
  private_under_contract: 'Privée sous contrat',
  public: 'Publique',
  other: 'Autre',
};

function humanizeSchoolType(type: string): string {
  const key = type.toLowerCase().trim();
  if (SCHOOL_TYPE_LABELS[key]) return SCHOOL_TYPE_LABELS[key];
  const s = key.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function initials(firstname?: string, lastname?: string): string {
  const f = firstname?.[0] ?? '';
  const l = lastname?.[0] ?? '';
  return `${f}${l}`.toUpperCase() || '—';
}

/** Formate un horodatage ISO en date + heure françaises (ajouts automatiques). */
function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Formate une date planifiée (AAAA-MM-JJ) en libellé long français. */
function formatPlannedDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, (m ?? 1) - 1, d ?? 1);
  return date.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('action.migration_v1_v2.manage');
  const canReset = hasPermission('action.migration_v1_v2.reset');
  const assignableUsers = useAssignableUsers(canManage);

  const [project, setProject] = useState<MigrationProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Réparation des parents fusionnés : `report` porte d'abord la simulation,
  // puis le résultat réel après confirmation.
  const [parentRepairReport, setParentRepairReport] = useState<CollapsedParentRepairReport | null>(null);
  const [parentRepairBusy, setParentRepairBusy] = useState(false);
  const [showParentRepair, setShowParentRepair] = useState(false);
  // Suppression des fiches en double : même cinématique — simulation d'abord,
  // résultat réel après confirmation.
  const [duplicateRepairReport, setDuplicateRepairReport] =
    useState<DuplicateUserRepairReport | null>(null);
  const [duplicateRepairBusy, setDuplicateRepairBusy] = useState(false);
  const [showDuplicateRepair, setShowDuplicateRepair] = useState(false);
  // Verrouillage facturation V1 : simulation d'abord, verrouillage (ou
  // rétablissement) après confirmation. Seule action qui écrit dans la V1.
  const [lockdownReport, setLockdownReport] = useState<V1BillingLockdownReport | null>(null);
  const [lockdownBusy, setLockdownBusy] = useState(false);
  const [showLockdown, setShowLockdown] = useState(false);
  const [runningStepId, setRunningStepId] = useState<number | null>(null);
  const [filter, setFilter] = useState<FilterBucket>('all');
  const [v2Ready, setV2Ready] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showRoleMappings, setShowRoleMappings] = useState(false);
  const [mappingLot, setMappingLot] = useState<MigrationStepExecution | null>(null);
  const [refreshingCounts, setRefreshingCounts] = useState(false);
  const [refreshWarnings, setRefreshWarnings] = useState<string[]>([]);
  // Rattrapage automatique des utilisateurs déclenché à la main depuis l'en-tête
  // (même traitement que la tâche programmée nocturne).
  const [catchingUp, setCatchingUp] = useState(false);
  const [backfillingBirthPlaces, setBackfillingBirthPlaces] = useState(false);
  const [birthPlacePreview, setBirthPlacePreview] =
    useState<BirthPlaceBackfillResult | null>(null);
  const [birthPlacePage, setBirthPlacePage] = useState(0);
  const [catchUpMessage, setCatchUpMessage] = useState('');
  const [estimatingStepId, setEstimatingStepId] = useState<number | null>(null);
  const [mappingStatus, setMappingStatus] = useState<Record<string, { total: number; mapped: number }>>({});
  const [roleMappingStatus, setRoleMappingStatus] = useState<{ total: number; mapped: number } | null>(null);
  const [mappingReloadTick, setMappingReloadTick] = useState(0);
  // Warning « annule et remplace » du lot Emplois du temps : des créneaux
  // existent déjà côté V2 → confirmation d'écrasement avant run. La promesse
  // du run en attente est résolue par les boutons du dialog.
  const [schedulesConfirm, setSchedulesConfirm] = useState<{
    step: MigrationStepExecution;
    status: SchedulesV2Status;
    resolve: (replace: boolean) => void;
  } | null>(null);
  const [purgingSchedules, setPurgingSchedules] = useState(false);
  // Bypass d'un lot incomplet : dialog de confirmation avant de passer le lot
  // en `bypassed` (statut qui débloque les lots dépendants).
  const [bypassConfirm, setBypassConfirm] = useState<MigrationStepExecution | null>(null);
  const [bypassing, setBypassing] = useState(false);
  // « Recommencer de zéro » : purge ADM + reset tenant V2. Action destructrice
  // → dialog de confirmation avec saisie du code de l'école.
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [restartConfirmText, setRestartConfirmText] = useState('');
  const [restarting, setRestarting] = useState(false);
  // Mode choisi dans le dialog. Les deux purgent le dossier ADM + suppriment le
  // tenant V2 et son utilisateur de base (même endpoint) ; seule la suite change :
  //  - « full » : retour au workflow de création V2 (wizard) pour tout reconfigurer,
  //    sans redéploiement automatique ;
  //  - « migration_only » : redéploiement automatique avec la config conservée
  //    (offre/modules), dialog de progression ouvert seul.
  const [restartMode, setRestartMode] = useState<'full' | 'migration_only'>('full');
  // Incrémenté après un « recommencer de zéro » réussi pour déclencher le
  // redéploiement automatique du tenant V2 (ouverture du dialog de progression).
  const [autoDeploySignal, setAutoDeploySignal] = useState(0);
  // Orchestration séquentielle par groupe.
  const [runningGroup, setRunningGroup] = useState<string | null>(null);
  const [groupStop, setGroupStop] = useState<
    { category: string | null; stepId: number; reason: 'failed' | 'blocked'; message: string } | null
  >(null);
  const abortRef = useRef(false);

  const projectId = Number(id);

  /**
     * @param silent Si true, ne déclenche pas l'état loading (utile pour les
     * refetch en arrière-plan après actions, pour éviter le démontage de la
     * page entière et le remount des sous-composants comme V2TargetStatusCard).
     */
  const fetchProject = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const proj = await migrationV1V2Api.getProject(projectId);
      setProject(proj);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      setError(
        status === 403
          ? "Vous n'avez pas accès à ce dossier de migration : il est réservé au responsable assigné et aux administrateurs."
          : 'Projet non trouvé'
      );
    } finally {
      if (!silent) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  /**
   * Comptage V1 « live » des lots utilisateurs, lancé automatiquement à
   * l'ouverture du dossier : fait apparaître « + N à migrer » sur les élèves /
   * parents / photos saisis en V1 APRÈS la migration, sans avoir à cliquer sur
   * « Charger les données V1 ».
   *
   * Volontairement détaché du chargement du projet — l'affichage du dossier ne
   * doit jamais attendre (ni échouer sur) une base V1 lente ou injoignable — et
   * silencieux : seuls les compteurs sont fusionnés dans l'état, sans écraser
   * le reste du projet (un run peut être en cours).
   */
  const sourceCountsRequestedFor = useRef<number | null>(null);
  useEffect(() => {
    const id = project?.id;
    if (!id || sourceCountsRequestedFor.current === id) return;
    sourceCountsRequestedFor.current = id;

    let cancelled = false;
    (async () => {
      try {
        const res = await migrationV1V2Api.refreshProjectSourceCounts(id);
        if (cancelled) return;
        const counters = new Map(
          (res.data.stepExecutions ?? []).map((s) => [s.id, s])
        );
        setProject((prev) => {
          if (!prev || prev.id !== id || !prev.stepExecutions) return prev;
          return {
            ...prev,
            stepExecutions: prev.stepExecutions.map((s) => {
              const fresh = counters.get(s.id);
              return fresh
                ? {
                    ...s,
                    sourceRecordCount: fresh.sourceRecordCount,
                    newSourceRecords: fresh.newSourceRecords,
                    sourceCountedAt: fresh.sourceCountedAt,
                    // Sur un lot jamais lancé, le comptage live tient lieu
                    // d'estimation : le volume affiché suit la V1.
                    totalRecords: fresh.totalRecords,
                    countEstimatedAt: fresh.countEstimatedAt,
                  }
                : s;
            }),
          };
        });
      } catch {
        // V1 injoignable : on reste sur les compteurs du dernier run.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [project?.id]);

  useBreadcrumbLabel(project ? `${project.school.code} — ${project.school.name}` : null);

  const steps = useMemo(() => {
    const list = project?.stepExecutions ?? [];
    return [...list].sort(
      (a, b) => (a.stepDefinition.sortOrder ?? 0) - (b.stepDefinition.sortOrder ?? 0)
    );
  }, [project?.stepExecutions]);

  // Codes des lots nécessitant un mapping référentiel (cycles, niveaux…).
  // Clé stable (string) pour ne pas recharger l'état du mapping à chaque
  // mise à jour optimiste des compteurs de lots.
  const mappingCodesKey = useMemo(
    () =>
      [...new Set(steps.map((s) => s.stepDefinition.code).filter((c) => LOTS_WITH_MAPPING.has(c)))]
        .sort()
        .join(','),
    [steps]
  );

  const schoolId = project?.school.id;
  const schoolIsEn = project?.school.isEducationNationale ?? true;

  // Charge l'état de complétude du mapping pour chaque lot concerné.
  // Rechargé quand l'école change, quand la liste des lots à mapper change,
  // ou après édition d'un mapping (mappingReloadTick).
  useEffect(() => {
    if (!schoolId || !mappingCodesKey) {
      setMappingStatus({});
      return;
    }
    let cancelled = false;
    const codes = mappingCodesKey.split(',');
    (async () => {
      const entries = await Promise.all(
        codes.map(async (code) => {
          const cfg = LOT_MAPPING_REGISTRY[code];
          if (!cfg) return null;
          // École non-EN : le mapping référentiel (cycle/niveau/matière) est
          // un passe-plat facultatif (label V1 conservé, renommable) — il ne
          // conditionne pas le lancement du lot, donc pas de complétude.
          if (!schoolIsEn && cfg.type !== 'custom_field') return null;
          try {
            // Type `custom_field` : pas d'override referentiel classique,
            // on lit la liste V1 customfields + mapping V2 via endpoint dédié.
            if (cfg.type === 'custom_field') {
              const form: 1 | 2 = code === 'students.eleves' ? 1 : 2;
              const r = await migrationV1V2Api.getCustomFields(schoolId, form);
              return [code, { total: r.total, mapped: r.mapped }] as const;
            }
            // Type `matiere` (lot `school.subjects`, écoles EN) : depuis la
            // refonte du mapping, le code BCN se choisit par ASSOCIATION
            // matière × niveau (type `matiere_niveau`) et le nom V2 se déduit
            // du code — le type `matiere` n'a plus de `targetCode`. La
            // complétude se mesure donc sur les associations : une association
            // est satisfaite si son niveau ne porte PAS de MEF (aucun code BCN
            // requis, ex. maternelle/primaire — le loader n'envoie rien) ou si
            // elle porte un `enCode`. Même règle que `SubjectLoader`.
            if (cfg.type === 'matiere') {
              const view = await migrationV1V2Api.getMatiereNiveauView(schoolId);
              const total = view.associations.length;
              const mapped = view.associations.filter(
                (a) => !a.mef || (a.enCode !== null && a.enCode !== '')
              ).length;
              return [code, { total, mapped }] as const;
            }
            const res = await migrationV1V2Api.getReferentialOverrides(schoolId, cfg.type, true);
            const total = res.mappings.length;
            const hasTarget = (m: (typeof res.mappings)[number]) =>
              Boolean(m.targetCode ?? m.globalTargetCode);
            // Niveaux (EN) : le cycle fait partie de la complétude du mapping.
            // Le drawer expose une colonne Cycle (override `niveau_cycle` sinon
            // cycle V1 mappé) ; un niveau sans cycle résolu doit laisser le chip
            // en « partiel » plutôt que vert, même si le niveau V2 est choisi.
            const mapped =
              cfg.type === 'niveau'
                ? res.mappings.filter((m) => hasTarget(m) && Boolean(m.cycleCode)).length
                : res.mappings.filter(hasTarget).length;
            return [code, { total, mapped }] as const;
          } catch {
            return null;
          }
        })
      );
      if (!cancelled) {
        setMappingStatus(
          Object.fromEntries(entries.filter((e): e is [string, { total: number; mapped: number }] => e !== null))
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolId, schoolIsEn, mappingCodesKey, mappingReloadTick]);

  // Complétude du mapping des rôles (type 'role', global à l'école), affichée
  // en chip dans la colonne Mapping de l'en-tête du groupe Utilisateurs.
  useEffect(() => {
    if (!schoolId) {
      setRoleMappingStatus(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await migrationV1V2Api.getReferentialOverrides(schoolId, 'role', true);
        if (!cancelled) {
          setRoleMappingStatus({
            total: res.mappings.length,
            mapped: res.mappings.filter((m) => m.targetCode ?? m.globalTargetCode).length,
          });
        }
      } catch {
        if (!cancelled) setRoleMappingStatus(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolId, mappingReloadTick]);

  // Lots dans le périmètre de migration : on exclut les catégories dont le
  // module V2 n'est pas souscrit par l'école (et les lots à surcharge par code,
  // cf. STEP_TO_V2_MODULES). Ces lots hors périmètre sont masqués dans la table
  // (voir LotsTable) et ne doivent donc pas non plus compter dans les stats
  // d'en-tête ni la barre de filtres, pour que les compteurs reflètent
  // uniquement le périmètre réel du déploiement.
  const activeV2Modules = project?.school.activeV2Modules;
  const inPerimeterSteps = useMemo(
    () =>
      steps.filter(
        (s) => !isStepOutOfPerimeter(s.stepDefinition.category, s.stepDefinition.code, activeV2Modules)
      ),
    [steps, activeV2Modules]
  );

  // Lots « disponibles prochainement » : affichés grisés dans la table (vue
  // « Tous ») mais désactivés — exclus des stats d'en-tête, des filtres et de
  // la progression globale, comme le hors périmètre.
  const countableSteps = useMemo(
    () =>
      inPerimeterSteps.filter(
        (s) => !isStepComingSoon(s.stepDefinition.category, s.stepDefinition.code)
      ),
    [inPerimeterSteps]
  );

  const buckets = useMemo(() => {
    const counts: Record<LotStatusBucket, number> = {
      pending: 0,
      running: 0,
      review: 0,
      failed: 0,
      completed: 0,
    };
    // Bucket « effectif » : un lot vide estimé (« aucune donnée ») compte
    // comme migré — cohérent avec les groupes, le statut projet back et la
    // board card.
    for (const s of countableSteps) counts[effectiveBucketOfStep(s)]++;
    return counts;
  }, [countableSteps]);

  const filteredSteps = useMemo(() => {
    // Vue « Tous » : lots « disponibles prochainement » inclus (rendus grisés
    // par LotsTable). Les autres filtres ne portent que sur les lots actifs.
    if (filter === 'all') return inPerimeterSteps;
    if (filter === 'review') {
      // « À traiter » regroupe review + failed
      return countableSteps.filter((s) => {
        const b = effectiveBucketOfStep(s);
        return b === 'review' || b === 'failed';
      });
    }
    return countableSteps.filter((s) => effectiveBucketOfStep(s) === filter);
  }, [inPerimeterSteps, countableSteps, filter]);

  // Progression globale du déploiement, calculée sur les enregistrements des
  // lots EN PÉRIMÈTRE (mêmes segments que les barres du tableau et le calcul
  // back de la board card). Évolue au fil des migrations (mises à jour
  // optimistes du state). Repli sur le ratio de lots terminés si aucun volume
  // n'est encore estimé.
  const globalProgress = useMemo(() => {
    let total = 0;
    let success = 0;
    let review = 0;
    let failed = 0;
    let excluded = 0;
    let completedLots = 0;
    for (const s of countableSteps) {
      total += s.totalRecords;
      success += s.successRecords;
      review += s.pendingReviewRecords;
      failed += s.failedRecords;
      excluded += s.excludedRecords ?? 0;
      if (effectiveBucketOfStep(s) === 'completed') completedLots++;
    }
    const done = success + review + failed + excluded;
    // Volumétrie estimée → % = taux de réussite par enregistrements (succès /
    // total hors exclus, même convention que le tableau des lots et le
    // rapport : les échecs ne comptent jamais comme de l'avancement). Sinon,
    // repli sur le ratio de lots terminés (avant « Charger les données V1 »).
    const byRecords = total > 0;
    const effectiveTotal = total - excluded;
    // Précision maximale conservée : l'arrondi (à une décimale) n'a lieu
    // qu'à l'affichage. Ainsi le seuil « >= 100 » (couleur verte) ne se
    // déclenche que sur un avancement RÉELLEMENT complet, sans qu'un
    // 99,96 % arrondi ne fasse croire à une migration terminée.
    const pct = byRecords
      ? effectiveTotal > 0
        ? (success / effectiveTotal) * 100
        : 100
      : countableSteps.length > 0
        ? (completedLots / countableSteps.length) * 100
        : 0;
    return { total, success, review, failed, excluded, done, pct, byRecords };
  }, [countableSteps]);

  /**
   * Migre un lot en bouclant sur run-chunk jusqu'à `done`. Met à jour le state
   * de façon optimiste entre chaque tranche et renvoie la dernière version du
   * lot (avec son statut final). Ne gère ni `runningStepId` ni `error` :
   * c'est la responsabilité de l'appelant (lot unique ou orchestration groupe).
   */
  const runLotChunks = async (
    step: MigrationStepExecution,
    reconcile = false
  ): Promise<MigrationStepExecution> => {
    // Taille de tranche ADAPTATIVE. Le temps de traitement d'un record varie
    // énormément d'un lot à l'autre (un parent/élève = create V2 + géocodage
    // d'adresse ≈ plusieurs secondes ; un référentiel = quasi-instantané). Avec
    // une taille fixe de 10, un lot lourd atteint ~60 s/requête = le timeout de
    // la gateway → coupure réseau, puis chunks qui se chevauchent (400
    // « traitement déjà en cours »). On vise donc un temps de traitement serveur
    // MODÉRÉ par requête : on démarre petit et on ajuste selon le temps mesuré.
    // Ainsi chaque requête reste courte quelle que soit la latence V2, sans
    // jamais approcher le timeout.
    const MIN_LIMIT = 1;
    // Plafond volontairement élevé : sur un lot RAPIDE et VOLUMINEUX (ex.
    // activités Montessori ≈ 0,3 s/record, 2000-5000+ lignes ; liens
    // activité↔compétence ; compétences), de grosses tranches réduisent
    // d'autant le nombre de requêtes — donc d'allers-retours HTTP ET de
    // rechargements d'état serveur par chunk (l'arbre / les listes V2 sont
    // réindexés à chaque process). Aucun risque pour les lots LENTS :
    // l'adaptation ci-dessous rétrécit la tranche dès qu'une requête dépasse
    // SLOW_MS, donc on reste toujours SOUS le timeout gateway/axios (~55 s)
    // quelle que soit la latence V2.
    const MAX_LIMIT = 100;
    const SLOW_MS = 40000; // au-delà : on réduit la tranche (marge sous les ~55 s)
    const FAST_MS = 18000; // en-deçà : on l'augmente (bande cible ~18-40 s/req)
    const VERY_FAST_MS = 8000; // très rapide : on DOUBLE (montée express sur gros lots)
    // Sécurité : borne maxi d'itérations pour éviter une boucle infinie en cas
    // de bug serveur.
    const MAX_ITER = 100000;
    // Auto-résilience de la BOUCLE (pas seulement du chunk) : un lot volumineux
    // (2000+ activités) est piloté par le navigateur ; si le wrapper résilient
    // épuise ses retries sur une erreur TRANSITOIRE (gateway coupée, verrou
    // serveur qui traîne), on ne veut plus avorter tout le lot et forcer un
    // clic « Relancer ». On rétrécit la tranche, on patiente, et on REPREND —
    // la reprise est idempotente (records déjà migrés sautés côté serveur).
    // On n'abandonne que si l'échec transitoire persiste plusieurs fois de
    // suite (serveur réellement KO) ou sur une vraie erreur métier.
    const MAX_CONSECUTIVE_FAILURES = 6;
    let limit = 5;
    let iter = 0;
    let latest = step;
    let done = false;
    let consecutiveFailures = 0;
    while (!done && iter < MAX_ITER) {
      iter++;
      const startedAt = performance.now();
      try {
        const res = await migrationV1V2Api.runStepChunkResilient(step.id, limit, reconcile);
        consecutiveFailures = 0;
        const elapsed = performance.now() - startedAt;
        latest = res.step;
        setProject((prev) => {
          if (!prev || !prev.stepExecutions) return prev;
          return {
            ...prev,
            stepExecutions: prev.stepExecutions.map((s) =>
              s.id === res.step.id ? { ...s, ...res.step } : s
            ),
          };
        });
        done = res.done;
        // Ajuste la tranche suivante : divise par deux si la requête a été
        // longue (records coûteux) ; sinon on l'augmente — en DOUBLANT tant que
        // c'est très rapide (montée express : 5→10→20→40→80→100 en ~5 requêtes
        // sur un gros lot rapide), puis par paliers en approchant la bande
        // cible. `elapsed` inclut d'éventuels retards de retry → biais
        // conservateur (on réduit), ce qui est le comportement voulu.
        if (elapsed > SLOW_MS) limit = Math.max(MIN_LIMIT, Math.floor(limit / 2));
        else if (elapsed < VERY_FAST_MS) limit = Math.min(MAX_LIMIT, limit * 2);
        else if (elapsed < FAST_MS) limit = Math.min(MAX_LIMIT, limit + 5);
      } catch (err) {
        // Erreur métier réelle (400 autre, 403, 404…) : on remonte.
        if (!migrationV1V2Api.isTransientChunkError(err)) throw err;
        // Échec transitoire ayant épuisé les retries du chunk : on ne lâche pas
        // la boucle. Trop d'échecs d'affilée = serveur vraiment indisponible.
        consecutiveFailures++;
        if (consecutiveFailures > MAX_CONSECUTIVE_FAILURES) throw err;
        // Tranche plus petite (moins de charge par requête) + attente croissante,
        // puis on retente le même point (le serveur reprend là où il en est).
        limit = Math.max(MIN_LIMIT, Math.floor(limit / 2));
        await new Promise((r) => setTimeout(r, Math.min(60000, 5000 * consecutiveFailures)));
      }
    }
    return latest;
  };

  /**
   * Pré-check « annule et remplace » du lot Emplois du temps. Les créneaux
   * n'ont pas de clé naturelle côté V2 : re-migrer sans purge dupliquerait
   * l'emploi du temps. Si des créneaux ÉTRANGERS au lot existent déjà
   * (migration d'un projet resetté OU saisie directe en V2), on affiche un
   * warning et on propose l'écrasement : purge V2 (agenda actif) + purge des
   * records du lot, puis run complet.
   *
   * Une REPRISE ne passe pas par là (`needsReplace` faux dès qu'un record est
   * migré) : les créneaux V2 comptés sont les occurrences de nos propres
   * records, que l'extraction préserve au re-run. Sans ce discriminant, un lot
   * interrompu restait bloqué — la purge proposée répond 501.
   *
   * Renvoie `false` si l'utilisateur annule (le run est abandonné).
   */
  const prepareSchedulesRun = async (step: MigrationStepExecution): Promise<boolean> => {
    if (step.stepDefinition.code !== 'school.schedules') return true;
    const status = await migrationV1V2Api.getSchedulesV2Status(step.id);
    // Repli sur `existingSlots` si le backend ne renvoie pas encore le flag.
    const needsReplace = status.needsReplace ?? status.existingSlots > 0;
    if (!needsReplace) return true;

    const replace = await new Promise<boolean>((resolve) =>
      setSchedulesConfirm({ step, status, resolve })
    );
    if (!replace) return false;

    setPurgingSchedules(true);
    try {
      await migrationV1V2Api.purgeSchedulesV2(step.id);
    } finally {
      setPurgingSchedules(false);
      setSchedulesConfirm(null);
    }
    return true;
  };

  const handleRunStep = async (
    step: MigrationStepExecution,
    reconcile = false
  ) => {
    if (!project) return;
    setError('');
    // Feedback IMMÉDIAT : le lancement n'est pas instantané (pré-check créneaux,
    // premier chunk réseau). On bascule le lot en « en cours » dès le clic pour
    // que le bouton s'anime/se désactive et que la barre repasse à 0, sans
    // attendre le retour serveur. Remis à null si le pré-check échoue/annule.
    setRunningStepId(step.id);
    try {
      if (!(await prepareSchedulesRun(step))) {
        setRunningStepId(null);
        return;
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Vérification des créneaux V2 impossible');
      setSchedulesConfirm(null);
      setRunningStepId(null);
      return;
    }
    try {
      await runLotChunks(step, reconcile);
      // Refresh silencieux à la fin : récupère les compteurs projet sans
      // démonter la page (sinon V2TargetStatusCard re-fetcherait son
      // /v2-progress, donnant l'illusion d'un reload). Cela rafraîchit le
      // statut de tous les lots → les lots dépendants de celui-ci voient leurs
      // prérequis satisfaits et se débloquent automatiquement.
      await fetchProject(true);
      // Relance aussi la vérification du mapping référentiel : un lot
      // référentiel fraîchement migré peut débloquer ses dépendants côté
      // mapping (et pas seulement côté dépendances).
      setMappingReloadTick((t) => t + 1);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Erreur lors du lancement du lot');
      await fetchProject(true);
    } finally {
      setRunningStepId(null);
    }
  };

  /**
   * Passe un lot en `bypassed` après confirmation (dialog). Les lots dépendants
   * se débloquent immédiatement ; le lot reste relançable plus tard.
   */
  const confirmBypassStep = async () => {
    if (!bypassConfirm) return;
    setBypassing(true);
    setError('');
    try {
      await migrationV1V2Api.bypassStep(bypassConfirm.id);
      setBypassConfirm(null);
      await fetchProject(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Impossible de bypasser ce lot');
      setBypassConfirm(null);
    } finally {
      setBypassing(false);
    }
  };

  /**
   * Migre séquentiellement tous les lots d'un groupe, dans l'ordre `sortOrder`.
   * S'arrête au premier lot bloqué (dépendances/mapping) ou dont le statut
   * final n'est pas `completed` (échec ou erreurs partielles). L'erreur est
   * exposée via `groupStop` (affichée sur la card du groupe et du lot).
   */
  const handleRunGroup = async (category: string | null) => {
    if (!project || runningGroup) return;
    setError('');
    setGroupStop(null);
    setRunningGroup(category ?? '__none__');
    abortRef.current = false;

    // Lots du groupe (sur l'ensemble non filtré), triés par sortOrder.
    const groupSteps = steps
      .filter((s) => (s.stepDefinition.category ?? null) === category)
      .sort((a, b) => (a.stepDefinition.sortOrder ?? 0) - (b.stepDefinition.sortOrder ?? 0));

    // Statuts locaux tenus à jour au fil de l'eau (dépendances inter-lots).
    const statusById = new Map(steps.map((s) => [s.id, s.status]));
    const stepByCode = new Map(steps.map((s) => [s.stepDefinition.code, s]));
    const DEP_OK = ['completed', 'completed_with_errors', 'bypassed'];

    try {
      for (const step of groupSteps) {
        if (abortRef.current) break;
        // Lot « disponible prochainement » : désactivé, jamais enchaîné.
        if (isStepComingSoon(step.stepDefinition.category, step.stepDefinition.code)) continue;
        // Lot hors périmètre (module V2 non souscrit) : masqué dans la table,
        // il ne doit pas être enchaîné par « Migrer le groupe ».
        if (isStepOutOfPerimeter(step.stepDefinition.category, step.stepDefinition.code, activeV2Modules))
          continue;
        const st = statusById.get(step.id);
        // Un lot `completed` qui garde des lignes non traitées (exclu ré-inclus
        // à la main) reste à reprendre : la table l'affiche « À migrer », le
        // bouton de groupe doit donc l'enchaîner comme les autres.
        if ((st === 'completed' && unprocessedRecords(step) === 0) || st === 'skipped' || st === 'bypassed')
          continue;
        // Lot « aucune donnée » (0 ligne estimée et rien de neuf côté V1) :
        // rien à migrer, on passe.
        if (isLotEmpty(step)) continue;

        // Blocage : dépendances non satisfaites ou mapping incomplet.
        // Un lot vide (0 ligne V1, comptage fait) satisfait la dépendance :
        // rien à migrer (ex. aucun regroupement de matières → groupe « Général »).
        const depBlock = (step.stepDefinition.dependencies ?? []).filter((code) => {
          const dep = stepByCode.get(code);
          if (!dep) return true;
          if (isLotEmpty(dep)) return false;
          const ds = statusById.get(dep.id);
          return !ds || !DEP_OK.includes(ds);
        });
        const ms = mappingStatus[step.stepDefinition.code];
        const mappingIncomplete = !!ms && ms.total > 0 && ms.mapped < ms.total;
        if (depBlock.length > 0 || mappingIncomplete) {
          setGroupStop({
            category,
            stepId: step.id,
            reason: 'blocked',
            message: mappingIncomplete
              ? `« ${step.stepDefinition.name} » est bloqué : mapping incomplet (${ms!.mapped}/${ms!.total}).`
              : `« ${step.stepDefinition.name} » est bloqué : à migrer d'abord — ${depBlock
                  .map((c) => stepByCode.get(c)?.stepDefinition.name ?? c)
                  .join(', ')}.`,
          });
          break;
        }

        // Lot Emplois du temps : warning « annule et remplace » si des
        // créneaux existent déjà côté V2. Un refus arrête l'orchestration
        // du groupe sur ce lot (comme un blocage).
        try {
          if (!(await prepareSchedulesRun(step))) {
            setGroupStop({
              category,
              stepId: step.id,
              reason: 'blocked',
              message: `« ${step.stepDefinition.name} » : remplacement des créneaux V2 refusé — lot non lancé.`,
            });
            break;
          }
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } }; message?: string };
          setGroupStop({
            category,
            stepId: step.id,
            reason: 'failed',
            message:
              err.response?.data?.error ||
              err.message ||
              `Vérification des créneaux V2 impossible pour « ${step.stepDefinition.name} ».`,
          });
          setSchedulesConfirm(null);
          break;
        }

        setRunningStepId(step.id);
        try {
          const result = await runLotChunks(step);
          statusById.set(step.id, result.status);
          if (result.status !== 'completed') {
            setGroupStop({
              category,
              stepId: step.id,
              reason: 'failed',
              message:
                result.errorLog ||
                `Migration arrêtée sur « ${step.stepDefinition.name} » (${result.status}).`,
            });
            break;
          }
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } }; message?: string };
          setGroupStop({
            category,
            stepId: step.id,
            reason: 'failed',
            message:
              err.response?.data?.error ||
              err.message ||
              `Migration arrêtée sur « ${step.stepDefinition.name} ».`,
          });
          break;
        } finally {
          setRunningStepId(null);
        }
      }
      await fetchProject(true);
      // Re-vérifie le mapping référentiel après l'enchaînement : débloque les
      // lots dépendants dont un prérequis référentiel vient d'être migré.
      setMappingReloadTick((t) => t + 1);
    } finally {
      setRunningGroup(null);
      setRunningStepId(null);
    }
  };

  const handleAbortGroup = () => {
    abortRef.current = true;
  };

  const handleViewStep = (step: MigrationStepExecution) => {
    navigate(`/migration-v1-v2/${projectId}/steps/${step.id}`);
  };

  const handleRefreshCounts = async () => {
    if (!project) return;
    try {
      setRefreshingCounts(true);
      setRefreshWarnings([]);
      const res = await migrationV1V2Api.refreshProjectCounts(project.id);
      setProject(res.data);
      if (res.warnings && res.warnings.length > 0) {
        setRefreshWarnings(res.warnings);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Erreur lors du rafraîchissement');
    } finally {
      setRefreshingCounts(false);
    }
  };

  /**
   * Rattrapage manuel des utilisateurs : reprend les personnes inscrites en V1
   * depuis la migration (création seule, aucune fiche existante modifiée) puis
   * les rattache à leur classe et à leurs groupes. Peut durer plusieurs minutes.
   */
  /**
   * Deux temps volontaires : la simulation annonce ce qui serait écrit, champ
   * par champ ; l'écriture n'a lieu qu'après confirmation dans la modale.
   */
  const handleBirthPlaceBackfill = async () => {
    if (!project) return;
    setError('');
    setCatchUpMessage('');
    setBackfillingBirthPlaces(true);
    try {
      const preview = await migrationV1V2Api.runProjectBirthPlaceBackfill(project.id, true);
      if (preview.candidates === 0) {
        setCatchUpMessage('Aucun lieu de naissance à reprendre : la V1 n’en a pas pour ces élèves.');
        return;
      }
      setBirthPlacePage(0);
      setBirthPlacePreview(preview);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(
        err.response?.data?.error || err.message || 'La simulation des lieux de naissance a échoué'
      );
    } finally {
      setBackfillingBirthPlaces(false);
    }
  };

  const confirmBirthPlaceBackfill = async () => {
    if (!project) return;
    setBirthPlacePreview(null);
    setBackfillingBirthPlaces(true);
    try {
      const res = await migrationV1V2Api.runProjectBirthPlaceBackfill(project.id, false);
      setCatchUpMessage(
        `${res.written} élève(s) repris, ${res.untouched} fiche(s) déjà renseignée(s) laissée(s) intacte(s)` +
          `${res.failed > 0 ? `, ${res.failed} en échec` : ''}.`
      );
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(
        err.response?.data?.error || err.message || 'Le rattrapage des lieux de naissance a échoué'
      );
    } finally {
      setBackfillingBirthPlaces(false);
    }
  };

  const handleAutoCatchUp = async () => {
    if (!project) return;
    setError('');
    setCatchUpMessage('');
    setCatchingUp(true);
    try {
      const res = await migrationV1V2Api.runProjectAutoCatchUp(project.id);
      setProject(res.project);
      setCatchUpMessage(
        res.data === null
          ? 'Aucun nouvel utilisateur côté V1 : rien à rattraper.'
          : `${res.data.addedRecords} utilisateur(s) ajouté(s)${
              res.data.failedRecords > 0 ? `, ${res.data.failedRecords} en échec` : ''
            }. Détail daté dans le rapport de migration.`
      );
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Le rattrapage des utilisateurs a échoué');
    } finally {
      setCatchingUp(false);
    }
  };

  /**
   * Ouvre l'écran de réparation des parents fusionnés en lançant d'abord une
   * SIMULATION : l'opérateur voit ce qui serait corrigé avant de décider.
   */
  const openParentRepair = useCallback(async () => {
    if (!project) return;
    setError('');
    setParentRepairReport(null);
    setShowParentRepair(true);
    setParentRepairBusy(true);
    try {
      setParentRepairReport(await migrationV1V2Api.repairCollapsedParents(project.id, false));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setShowParentRepair(false);
      setError(err.response?.data?.error || err.message || 'Analyse des parents fusionnés impossible');
    } finally {
      setParentRepairBusy(false);
    }
  }, [project]);

  /** Applique réellement la réparation, puis affiche le rapport définitif. */
  const applyParentRepair = useCallback(async () => {
    if (!project) return;
    setParentRepairBusy(true);
    try {
      setParentRepairReport(await migrationV1V2Api.repairCollapsedParents(project.id, true));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setShowParentRepair(false);
      setError(err.response?.data?.error || err.message || 'Réparation des parents fusionnés impossible');
    } finally {
      setParentRepairBusy(false);
    }
  }, [project]);

  /**
   * Ouvre l'écran de verrouillage de la facturation V1 en lisant d'abord
   * l'état RÉEL des interrupteurs V1 (simulation, rien n'est écrit).
   */
  const openLockdown = useCallback(async () => {
    if (!project) return;
    setError('');
    setLockdownReport(null);
    setShowLockdown(true);
    setLockdownBusy(true);
    try {
      setLockdownReport(await migrationV1V2Api.v1BillingLockdown(project.id, 'preview'));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setShowLockdown(false);
      setError(err.response?.data?.error || err.message || 'Lecture des réglages V1 impossible');
    } finally {
      setLockdownBusy(false);
    }
  }, [project]);

  /** Applique (ou ré-applique) réellement le verrouillage V1. */
  const applyLockdown = useCallback(async () => {
    if (!project) return;
    setLockdownBusy(true);
    try {
      setLockdownReport(await migrationV1V2Api.v1BillingLockdown(project.id, 'apply'));
      void fetchProject(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setShowLockdown(false);
      setError(err.response?.data?.error || err.message || 'Verrouillage V1 impossible');
    } finally {
      setLockdownBusy(false);
    }
  }, [project, fetchProject]);

  /** Rétablit les valeurs V1 d'origine mémorisées au verrouillage. */
  const restoreLockdown = useCallback(async () => {
    if (!project) return;
    setLockdownBusy(true);
    try {
      setLockdownReport(await migrationV1V2Api.v1BillingLockdown(project.id, 'restore'));
      void fetchProject(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setShowLockdown(false);
      setError(err.response?.data?.error || err.message || 'Rétablissement V1 impossible');
    } finally {
      setLockdownBusy(false);
    }
  }, [project, fetchProject]);

  /**
   * Ouvre l'écran de suppression des fiches en double en lançant d'abord une
   * SIMULATION : l'opérateur voit qui serait supprimé avant de décider.
   */
  const openDuplicateRepair = useCallback(async () => {
    if (!project) return;
    setError('');
    setDuplicateRepairReport(null);
    setShowDuplicateRepair(true);
    setDuplicateRepairBusy(true);
    try {
      setDuplicateRepairReport(await migrationV1V2Api.repairDuplicateUsers(project.id, false));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setShowDuplicateRepair(false);
      setError(err.response?.data?.error || err.message || 'Analyse des fiches en double impossible');
    } finally {
      setDuplicateRepairBusy(false);
    }
  }, [project]);

  /** Supprime réellement les fiches en double, puis affiche le rapport définitif. */
  const applyDuplicateRepair = useCallback(async () => {
    if (!project) return;
    setDuplicateRepairBusy(true);
    try {
      setDuplicateRepairReport(await migrationV1V2Api.repairDuplicateUsers(project.id, true));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setShowDuplicateRepair(false);
      setError(err.response?.data?.error || err.message || 'Suppression des fiches en double impossible');
    } finally {
      setDuplicateRepairBusy(false);
    }
  }, [project]);

  const handleRestartFromScratch = async () => {
    if (!project) return;
    setError('');
    setRestarting(true);
    try {
      // Les deux modes réinitialisent l'environnement V2 (purge ADM + suppression
      // du tenant + drop de l'utilisateur de base) via le même endpoint.
      const res = await migrationV1V2Api.restartFromScratch(project.id);
      setShowRestartConfirm(false);
      setRestartConfirmText('');

      if (res.v2Error) {
        // Purge ADM OK mais reset V2 en échec : on prévient et on recharge sans
        // enchaîner (ni wizard, ni redéploiement) — l'opérateur relancera le reset.
        setError(res.message);
        await fetchProject();
        return;
      }

      if (restartMode === 'full') {
        // Recréer tout l'environnement : on repart du workflow de création V2
        // (wizard « Passage en V2 ») pour re-qualifier offre/branding/établissements.
        // Pas de redéploiement automatique.
        navigate(
          `/schools/${project.school.code}/upgrade-v2?returnTo=${encodeURIComponent(
            `/migration-v1-v2/${project.id}`
          )}`
        );
        return;
      }

      // migration_only : redéploiement automatique avec la config conservée
      // (le dialog de progression s'ouvre seul via le signal). Refetch SILENCIEUX
      // impératif : un refetch non-silent démonte V2TargetStatusCard (skeleton
      // plein écran) et, au remount, le ref du signal se réinitialise à la valeur
      // déjà incrémentée → l'effet d'auto-déploiement ne se déclencherait jamais.
      setAutoDeploySignal((n) => n + 1);
      await fetchProject(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Erreur lors de la réinitialisation');
    } finally {
      setRestarting(false);
    }
  };

  const handleEstimateLot = async (step: MigrationStepExecution) => {
    setError('');
    setEstimatingStepId(step.id);
    try {
      const updated = await migrationV1V2Api.refreshStepCount(step.id);
      setProject((prev) => {
        if (!prev || !prev.stepExecutions) return prev;
        return {
          ...prev,
          stepExecutions: prev.stepExecutions.map((s) =>
            s.id === updated.id ? { ...s, ...updated } : s
          ),
        };
      });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || "Erreur lors de l'estimation du lot");
    } finally {
      setEstimatingStepId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24" />
        <Skeleton className="h-16" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/migration-v1-v2/board')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error || 'Projet non trouvé'}
        </div>
      </div>
    );
  }

  const stats = {
    total: countableSteps.length,
    completed: buckets.completed,
    running: buckets.running,
    review: buckets.review,
    failed: buckets.failed,
    pending: buckets.pending,
  };

  // `reset` est éligible : après un « Recommencer de zéro », le projet reste
  // dans ce statut jusqu'au premier run (le runner le repasse en `running`) —
  // v2Ready + isConfigured garantissent que le tenant a été redéployé et le
  // couple d'années reconfiguré.
  //
  // `completed` est éligible aussi : un projet terminé peut se retrouver avec
  // un lot en attente — nouveau lot ajouté au catalogue, ou lot devenu « en
  // périmètre » après coup (ex. le référentiel de compétences reclassé en
  // Évaluations). Sans ça, tous les boutons « Migrer » restaient grisés sans
  // explication, le seul contournement étant « Charger les données V1 » qui
  // recalcule le statut. Côté back, `MigrationRunner` accepte déjà de lancer un
  // lot sur un projet `completed` et `updateProjectTotals` remet le statut à
  // jour ensuite.
  const BIRTH_PLACE_PAGE_SIZE = 25;

  const RUNNABLE_PROJECT_STATUSES = [
    'draft',
    'ready',
    'running',
    'paused',
    'failed',
    'completed',
    'reset',
  ];

  const canRunSteps =
    hasPermission('action.migration_v1_v2.execute') &&
    RUNNABLE_PROJECT_STATUSES.includes(project.status) &&
    v2Ready &&
    project.isConfigured;

  // Le re-run « réconciliation » d'un lot déjà migré : mêmes conditions.
  const canReconcileSteps = canRunSteps;

  const owner = project.assignedTo;
  const school = project.school;

  return (
    <div className="space-y-6">
      {/* Header + V2 target — carte unique, 2/3 – 1/3 avec séparateur */}
      <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-100 dark:divide-slate-700">
      <div className="lg:col-span-2">
        <div className="p-5">
          {/* Zone haute : retour · logo · identité · (chips statut + fermer).
              flex-wrap : le cluster d'actions bascule sous l'identité quand la
              colonne est trop étroite (split V2 actif, sidebar ouverte). */}
          <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
            {/* Groupe identité — reste sur une ligne, garde une largeur mini */}
            <div className="flex min-w-0 grow basis-72 items-start gap-4">
            <Button variant="ghost" size="icon-sm" asChild className="mt-1 shrink-0">
              <Link to="/migration-v1-v2/board" aria-label="Retour au board">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>

            {/* Logo de l'école (ou placeholder neutre) */}
            <div className="shrink-0">
              {school.logoUrl ? (
                <img
                  src={school.logoUrl}
                  alt={`Logo ${school.name}`}
                  className="h-14 w-14 rounded-lg border border-gray-200 object-cover dark:border-slate-700"
                />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-gray-200 bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500">
                  <School className="h-7 w-7" />
                </div>
              )}
            </div>

            {/* Identité — occupe l'espace disponible */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {school.name}
                </h1>
                <Badge variant="outline" className="font-mono text-xs">
                  {school.code}
                </Badge>
              </div>

              {/* Métadonnées (casse normale, ton slate pour éviter l'uppercase global) */}
              <div className="mt-2 flex items-center gap-x-2.5 gap-y-1 text-sm text-slate-500 dark:text-slate-400 flex-wrap">
                {[
                  school.studentCount != null && (
                    <span key="students">
                      <strong className="font-semibold text-slate-700 dark:text-slate-200">
                        {school.studentCount}
                      </strong>{' '}
                      élèves
                    </span>
                  ),
                  school.type && <span key="type">{humanizeSchoolType(school.type)}</span>,
                  school.version && (
                    <span key="version">
                      V1 <strong className="font-semibold text-slate-700 dark:text-slate-200">{school.version}</strong>
                    </span>
                  ),
                ]
                  .filter(Boolean)
                  .map((node, i) => (
                    <span key={i} className="inline-flex items-center gap-x-2.5">
                      {i > 0 && <span className="text-slate-300 dark:text-slate-600">·</span>}
                      {node}
                    </span>
                  ))}
              </div>

              {/* Responsable + date de migration prévue : paires libellé/contrôle
                  sur leur propre ligne (le dot-separator au-dessus casse la mise
                  en page dès qu'il contient un champ de formulaire). */}
              <div className="mt-2.5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500 dark:text-slate-400">
                <div className="inline-flex items-center gap-2">
                  <span className="shrink-0">Responsable :</span>
                  {canManage ? (
                    <AssignUserControl
                      projectId={project.id}
                      assignedTo={owner}
                      users={assignableUsers}
                      onAssigned={(p) =>
                        setProject((prev) => (prev ? { ...prev, assignedTo: p.assignedTo } : prev))
                      }
                      onError={(m) => setError(m)}
                      triggerClassName="h-7 w-56 text-xs"
                    />
                  ) : owner ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Avatar className="h-5 w-5">
                        <AvatarFallback className="text-[10px] font-medium bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300">
                          {initials(owner.firstname, owner.lastname)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium text-slate-700 dark:text-slate-300">
                        {owner.firstname} {owner.lastname?.[0] ?? ''}.
                      </span>
                    </span>
                  ) : (
                    <span className="italic">non assigné</span>
                  )}
                </div>

                <div className="inline-flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 shrink-0">
                    <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
                    Date prévue :
                  </span>
                  {canManage ? (
                    <PlannedDateControl
                      projectId={project.id}
                      plannedMigrationDate={project.plannedMigrationDate}
                      onUpdated={(p) =>
                        setProject((prev) =>
                          prev ? { ...prev, plannedMigrationDate: p.plannedMigrationDate } : prev
                        )
                      }
                      onError={(m) => setError(m)}
                      inputClassName="h-7 w-40 text-xs"
                    />
                  ) : project.plannedMigrationDate ? (
                    <span className="font-medium text-slate-700 dark:text-slate-300">
                      {formatPlannedDate(project.plannedMigrationDate)}
                    </span>
                  ) : (
                    <span className="italic">non planifiée</span>
                  )}
                </div>
              </div>

              {/* Configuration des années scolaires */}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {project.isConfigured ? (
                  <button
                    onClick={() => setShowConfigDialog(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                    title="Modifier la configuration des années scolaires"
                  >
                    <CalendarRange className="h-3.5 w-3.5" />
                    <span>
                      V1 {project.sourceSchoolYearV1Label}
                      <span className="mx-1 text-gray-400">→</span>
                      V2 {project.targetSchoolYearV2Label}
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => setShowConfigDialog(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-700 hover:bg-orange-100 transition-colors"
                  >
                    <Settings2 className="h-3.5 w-3.5" />
                    Configurer les années scolaires
                  </button>
                )}
              </div>
            </div>
            </div>

            {/* Zone droite : chips de statut (en haut à droite) + fermer.
                ml-auto : reste calé à droite, y compris une fois passé à la ligne. */}
            <div className="flex items-start gap-2 ml-auto">
              <div className="flex items-center justify-end gap-2 flex-wrap">
                <Button size="sm" asChild title="Rapport d'audit de la migration (mis à jour au fil de l'avancement)">
                  <Link to={`/migration-v1-v2/${project.id}/report`}>
                    <FileText className="h-3.5 w-3.5" />
                    Rapport de migration
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openParentRepair}
                  title="Séparer les parents fusionnés (même e-mail en V1) et rectifier les rôles père/mère inversés"
                >
                  <Users className="h-3.5 w-3.5" />
                  Réparer les fiches parents
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openDuplicateRepair}
                  title="Supprimer les fiches créées deux fois par une tranche de migration interrompue"
                >
                  <CopyX className="h-3.5 w-3.5" />
                  Fiches en double
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openLockdown}
                  disabled={!V1_BILLING_LOCKDOWN_ENABLED}
                  title={
                    V1_BILLING_LOCKDOWN_ENABLED
                      ? 'Fermer la facturation et les paiements côté V1 (réversible) — à faire dès que la facturation démarre en V2, pour ne pas faire avancer la numérotation V1'
                      : 'Disponible prochainement : les écoles continuent à facturer en V1 pour le moment'
                  }
                >
                  <Lock className="h-3.5 w-3.5" />
                  Fermer la facturation V1
                </Button>
                {canReset && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/40"
                    onClick={() => {
                      setRestartMode('full');
                      setRestartConfirmText('');
                      setShowRestartConfirm(true);
                    }}
                    title="Recommencer la migration : recréer tout l'environnement V2 ou réinitialiser uniquement le dossier de migration"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Recommencer de zéro
                  </Button>
                )}
                <ProjectStatusBadge status={project.status} />
                {school.isEducationNationale && (
                  <span
                    className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300"
                    title="École Éducation Nationale — certains mappings de migration sont conditionnés par ce statut"
                  >
                    <Landmark className="h-3 w-3" />
                    Éducation Nationale
                  </span>
                )}
              </div>
              <Button variant="ghost" size="icon-sm" asChild>
                <Link to="/migration-v1-v2/board" aria-label="Fermer">
                  <X className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Ajouts automatiques d'utilisateurs : la V1 a continué d'inscrire des
            personnes après la bascule, la tâche programmée nocturne les a
            reprises. On le signale ici pour renvoyer vers le détail daté du
            rapport ; le bouton rejoue la même passe immédiatement. */}
        {project.autoCatchUp && (project.autoCatchUp.added > 0 || project.autoCatchUp.eligible) && (
          <div
            className={cn(
              'flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3 text-sm',
              project.autoCatchUp.added > 0
                ? 'border-sky-100 bg-sky-50/70 text-sky-900 dark:border-sky-900/60 dark:bg-sky-950/25 dark:text-sky-100'
                : 'border-gray-100 bg-gray-50/60 text-slate-600 dark:border-slate-700 dark:bg-slate-900/30 dark:text-slate-300'
            )}
          >
            <div className="flex items-start gap-2 min-w-0">
              <UserPlus
                className={cn(
                  'mt-0.5 h-4 w-4 shrink-0',
                  project.autoCatchUp.added > 0 ? 'text-sky-600 dark:text-sky-300' : 'text-slate-400'
                )}
              />
              <div className="min-w-0">
                {project.autoCatchUp.added > 0 ? (
                  <>
                    <strong className="font-semibold">
                      {project.autoCatchUp.added} utilisateur
                      {project.autoCatchUp.added > 1 ? 's' : ''} ajouté
                      {project.autoCatchUp.added > 1 ? 's' : ''} automatiquement
                    </strong>{' '}
                    après la migration
                    {project.autoCatchUp.lastRunAt && (
                      <> — dernier ajout le {formatDateTime(project.autoCatchUp.lastRunAt)}</>
                    )}
                    {project.autoCatchUp.failed > 0 && (
                      <span className="text-red-700 dark:text-red-300">
                        {' '}
                        · {project.autoCatchUp.failed} en échec
                      </span>
                    )}
                    <div className="text-xs text-sky-700/90 dark:text-sky-300/90">
                      Inscriptions faites en V1 depuis la bascule, reprises en création seule (aucune
                      fiche existante modifiée). Détail nominatif et daté dans l'historique.
                    </div>
                  </>
                ) : (
                  <>
                    <strong className="font-semibold">Rattrapage automatique actif</strong> — chaque nuit,
                    les utilisateurs inscrits en V1 depuis la migration sont ajoutés en V2 et rattachés à
                    leur classe.
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      Aucun ajout à ce jour.
                    </div>
                  </>
                )}
                {catchUpMessage && (
                  <div className="mt-1 text-xs font-medium text-slate-700 dark:text-slate-200">
                    {catchUpMessage}
                  </div>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {project.autoCatchUp.added > 0 && (
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/migration-v1-v2/rattrapage/ecoles/${project.id}`}>
                    <History className="h-3.5 w-3.5" />
                    Historique des ajouts
                  </Link>
                </Button>
              )}
              {canRunSteps && project.autoCatchUp.eligible && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAutoCatchUp}
                  disabled={catchingUp}
                  title="Rejouer maintenant le rattrapage des utilisateurs (création seule)"
                >
                  {catchingUp ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <UserPlus className="h-3.5 w-3.5" />
                  )}
                  {catchingUp ? 'Rattrapage…' : 'Rattraper maintenant'}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Reprise du lieu de naissance : la commune et le pays n'ont rejoint le
            catalogue des champs natifs qu'après la bascule de la plupart des
            écoles. Bandeau AUTONOME — surtout pas imbriqué dans celui du
            rattrapage des utilisateurs, qui ne s'affiche que pour les dossiers
            éligibles à cette passe : le bouton y devenait invisible là où il
            sert justement le plus. */}
        {canRunSteps && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/60 px-5 py-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/30 dark:text-slate-300">
            <div className="flex items-start gap-2 min-w-0">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <p className="min-w-0">
                Reprendre la commune et le pays de naissance des élèves depuis la V1.
                <span className="block text-xs text-slate-500 dark:text-slate-400">
                  Simulation d'abord ; les fiches déjà renseignées ne sont jamais modifiées.
                </span>
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBirthPlaceBackfill}
              disabled={backfillingBirthPlaces}
            >
              {backfillingBirthPlaces ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <MapPin className="h-3.5 w-3.5" />
              )}
              {backfillingBirthPlaces ? 'Reprise…' : 'Lieux de naissance'}
            </Button>
          </div>
        )}

        {/* Progression globale — gros pourcentage + barre segmentée. Ne compte
            que les lots en périmètre ; évolue au fil des migrations. */}
        {stats.total > 0 && (
          <div className="border-t border-gray-100 dark:border-slate-700 px-5 py-3.5 flex items-center gap-5">
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex items-center justify-between text-xs">
                <span className="font-medium tracking-wide text-slate-500 dark:text-slate-400">
                  Progression globale du déploiement
                </span>
                {globalProgress.byRecords && (
                  <span className="tabular-nums text-slate-500 dark:text-slate-400">
                    <strong className="font-semibold text-slate-700 dark:text-slate-200">
                      {globalProgress.done}
                    </strong>{' '}
                    / {globalProgress.total} enregistrements
                  </span>
                )}
              </div>
              {globalProgress.byRecords ? (
                <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-200/70 dark:bg-slate-700">
                  {globalProgress.success > 0 && (
                    <div
                      className="bg-emerald-500"
                      style={{ width: `${(globalProgress.success / globalProgress.total) * 100}%` }}
                    />
                  )}
                  {globalProgress.review > 0 && (
                    <div
                      className="bg-amber-500"
                      style={{ width: `${(globalProgress.review / globalProgress.total) * 100}%` }}
                    />
                  )}
                  {globalProgress.failed > 0 && (
                    <div
                      className="bg-red-500"
                      style={{ width: `${(globalProgress.failed / globalProgress.total) * 100}%` }}
                    />
                  )}
                  {globalProgress.excluded > 0 && (
                    <div
                      className="bg-slate-400"
                      style={{ width: `${(globalProgress.excluded / globalProgress.total) * 100}%` }}
                    />
                  )}
                </div>
              ) : (
                <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-200/70 dark:bg-slate-700">
                  <div className="bg-emerald-500" style={{ width: `${globalProgress.pct}%` }} />
                </div>
              )}
            </div>
            <div className="flex items-baseline gap-0.5 shrink-0 tabular-nums">
              <span
                className={cn(
                  'text-3xl font-bold leading-none',
                  globalProgress.pct >= 100 ? 'text-emerald-600' : 'text-gray-900 dark:text-gray-100'
                )}
              >
                {formatPercent(globalProgress.pct)}
              </span>
              <span className="text-lg font-semibold text-slate-400 dark:text-slate-500">%</span>
            </div>
          </div>
        )}

        {/* Stats bar */}
        <div className="border-t border-gray-100 dark:border-slate-700 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 divide-x divide-y sm:divide-y-0 divide-gray-100 dark:divide-slate-700">
          <HeaderStat label="Lots total" value={stats.total} />
          <HeaderStat label="Migrés" value={stats.completed} tone="success" />
          <HeaderStat label="En cours" value={stats.running} tone="orange" />
          <HeaderStat label="En erreur" value={stats.failed} tone="red" />
          <HeaderStat label="À migrer" value={stats.pending} />
        </div>
      </div>

        {/* V2 target status (right column, sans bordure propre) */}
        <div className="lg:col-span-1">
          <V2TargetStatusCard
            schoolCode={school.code}
            onReadyChange={setV2Ready}
            borderless
            activeV2Modules={school.activeV2Modules}
            autoDeploySignal={autoDeploySignal}
            onDeployDialogClose={() => fetchProject(true)}
          />
        </div>
      </div>

      {/* Section title + filters */}
      <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Lots à migrer
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Lancez, vérifiez ou rejouez chaque lot indépendamment.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshCounts}
              disabled={refreshingCounts || !project.isConfigured}
              title={
                !project.isConfigured
                  ? 'Configurez les années scolaires pour calculer la volumétrie'
                  : 'Compte les lignes V1 de chaque lot à migrer, et recompte la volumétrie live des lots utilisateurs même terminés (lecture seule, aucun appel V2)'
              }
            >
              {refreshingCounts ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Charger les données V1
            </Button>
            <span className="hidden sm:inline-block w-px h-5 bg-gray-200 dark:bg-slate-700 mx-1" />
            {FILTER_DEFS.map((f) => {
              const count =
                f.key === 'all'
                  ? stats.total
                  : f.key === 'review'
                  ? stats.review + stats.failed
                  : f.key === 'pending'
                  ? stats.pending
                  : f.key === 'running'
                  ? stats.running
                  : stats.completed;
              return (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={cn(
                    'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                    filter === f.key
                      ? 'bg-gray-100 text-gray-900 ring-1 ring-gray-300 dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-600'
                      : 'text-gray-600 hover:bg-gray-50 dark:hover:bg-slate-700'
                  )}
                >
                  {f.label}
                  <span
                    className={cn(
                      // `rounded-md` (et non `rounded-full`) : évite la règle globale
                      // de themes.css qui force padding/radius/taille sur les chips
                      // ronds. Pastille compacte qui épouse le nombre (largeur
                      // naturelle, padding serré).
                      'inline-flex items-center justify-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold leading-none',
                      filter === f.key ? 'bg-gray-200 text-slate-700 dark:bg-slate-600 dark:text-slate-200' : 'bg-gray-100 text-slate-500'
                    )}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <LotsTable
          steps={filteredSteps}
          allSteps={steps}
          onRun={handleRunStep}
          onView={handleViewStep}
          onEstimateLot={handleEstimateLot}
          estimatingStepId={estimatingStepId}
          runningStepId={runningStepId}
          canRun={canRunSteps}
          canReconcile={canReconcileSteps}
          categoryActions={[
            {
              label: 'Rôles',
              title: 'Configurer le mapping des rôles V1 → V2',
              category: 'users',
              onClick: () => setShowRoleMappings(true),
              mapping: roleMappingStatus ?? undefined,
            },
          ] satisfies CategoryAction[]}
          onConfigureMapping={(step) => setMappingLot(step)}
          mappingStatusByCode={mappingStatus}
          onBypass={canRunSteps ? (step) => setBypassConfirm(step) : undefined}
          onRunGroup={handleRunGroup}
          onAbortGroup={handleAbortGroup}
          runningGroup={runningGroup}
          groupStop={groupStop}
          activeV2Modules={school.activeV2Modules}
        />

        {refreshWarnings.length > 0 && (
          <div className="border-t border-orange-200 bg-orange-50 dark:bg-orange-950/20 px-5 py-3 text-sm text-orange-800 dark:text-orange-200">
            <div className="font-semibold flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4" />
              {refreshWarnings.length} lot(s) sans estimation
            </div>
            <ul className="list-disc pl-6 text-xs space-y-0.5">
              {refreshWarnings.map((w, i) => (
                <li key={i} className="font-mono">{w}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Fil de discussion (propriétaire ↔ administrateurs) */}
      <ProjectComments projectId={project.id} />

      {/* Warning « annule et remplace » — lot Emplois du temps */}
      {/* Aperçu du rattrapage : ce qui serait écrit, champ par champ, pour
          TOUS les élèves concernés. Hauteur bornée et pied fixe — une liste de
          plusieurs centaines de lignes poussait sinon les boutons hors écran,
          dans une fenêtre sans ascenseur. */}
      <AlertDialog
        open={!!birthPlacePreview}
        onOpenChange={(open) => {
          if (!open) setBirthPlacePreview(null);
        }}
      >
        <AlertDialogContent className="flex max-h-[85vh] max-w-3xl flex-col gap-3">
          <AlertDialogHeader className="shrink-0">
            <AlertDialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-sky-500" />
              Reprendre les lieux de naissance depuis la V1
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-sky-600">{birthPlacePreview?.candidates}</span>{' '}
              élève(s) à reprendre sur {birthPlacePreview?.eligible} migrés ;{' '}
              {birthPlacePreview?.nothingToImport} n'ont rien en V1. Le département est calculé par
              la V2 à partir de la commune ; une fiche déjà renseignée n'est jamais modifiée.
              {(birthPlacePreview?.lookupFailures ?? 0) > 0 && (
                <span className="mt-1 block font-medium text-orange-600">
                  {birthPlacePreview?.lookupFailures} élève(s) n'ont pas pu être résolus : la
                  nomenclature n'a pas répondu. Relancez pour les rattraper — leur donnée V1 existe.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-gray-200 dark:border-slate-700">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 text-slate-500 dark:bg-slate-900">
                <tr>
                  <th className="px-2 py-1 text-left font-medium">Élève</th>
                  <th className="px-2 py-1 text-left font-medium">Pays</th>
                  <th className="px-2 py-1 text-left font-medium">Commune</th>
                  <th className="px-2 py-1 text-left font-medium">Lieu de naissance</th>
                </tr>
              </thead>
              <tbody>
                {(birthPlacePreview?.samples ?? [])
                  .slice(
                    birthPlacePage * BIRTH_PLACE_PAGE_SIZE,
                    (birthPlacePage + 1) * BIRTH_PLACE_PAGE_SIZE
                  )
                  .map((sample: BirthPlaceBackfillResult['samples'][number], index: number) => {
                    // Rien en V1 : l'élève reste affiché, avec la raison. Le
                    // faire disparaître laissait l'opérateur sans explication
                    // sur l'écart entre élèves migrés et reprises.
                    const nothingToImport =
                      sample.countryCode === null &&
                      sample.communeCode === null &&
                      sample.lieu === null;

                    return (
                      <tr key={index} className="border-t border-gray-100 dark:border-slate-800">
                        <td className="whitespace-nowrap px-2 py-1">{sample.eleve}</td>
                        {sample.lookupFailed ? (
                          <td className="px-2 py-1 italic text-orange-600" colSpan={3}>
                            Nomenclature injoignable — à relancer
                          </td>
                        ) : nothingToImport ? (
                          <td className="px-2 py-1 italic text-slate-400" colSpan={3}>
                            Rien à importer — aucun lieu de naissance en V1
                          </td>
                        ) : (
                          <>
                            <td className="px-2 py-1 tabular-nums">{sample.countryCode ?? '—'}</td>
                            <td className="px-2 py-1 tabular-nums">{sample.communeCode ?? '—'}</td>
                            <td className="px-2 py-1">{sample.lieu ?? '—'}</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {(birthPlacePreview?.samples.length ?? 0) > BIRTH_PLACE_PAGE_SIZE && (
            <div className="flex shrink-0 items-center justify-between text-xs text-slate-500">
              <span className="tabular-nums">
                {birthPlacePage * BIRTH_PLACE_PAGE_SIZE + 1}–
                {Math.min(
                  (birthPlacePage + 1) * BIRTH_PLACE_PAGE_SIZE,
                  birthPlacePreview?.samples.length ?? 0
                )}{' '}
                sur {birthPlacePreview?.samples.length}
              </span>
              <span className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={birthPlacePage === 0}
                  onClick={() => setBirthPlacePage((page) => Math.max(0, page - 1))}
                >
                  Précédents
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={
                    (birthPlacePage + 1) * BIRTH_PLACE_PAGE_SIZE >=
                    (birthPlacePreview?.samples.length ?? 0)
                  }
                  onClick={() => setBirthPlacePage((page) => page + 1)}
                >
                  Suivants
                </Button>
              </span>
            </div>
          )}

          <AlertDialogFooter className="shrink-0">
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBirthPlaceBackfill}>
              Lancer la reprise
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!schedulesConfirm}
        onOpenChange={(open) => {
          if (!open && schedulesConfirm && !purgingSchedules) {
            schedulesConfirm.resolve(false);
            setSchedulesConfirm(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Des créneaux existent déjà sur la V2
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  L'agenda{' '}
                  <span className="font-medium">
                    « {schedulesConfirm?.status.agendaName ?? 'Emploi du temps'} »
                  </span>{' '}
                  contient déjà{' '}
                  <span className="font-semibold text-orange-600">
                    {schedulesConfirm?.status.existingSlots} créneau
                    {(schedulesConfirm?.status.existingSlots ?? 0) > 1 ? 'x' : ''}
                  </span>{' '}
                  (migration précédente ou saisie directe côté V2).
                </p>
                <p>
                  La migration des emplois du temps fonctionne en{' '}
                  <span className="font-semibold">annule et remplace</span> : tous les créneaux
                  existants de cet agenda seront supprimés, puis l'intégralité des créneaux V1 de
                  l'année source sera re-migrée.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={purgingSchedules}
              onClick={() => {
                schedulesConfirm?.resolve(false);
                setSchedulesConfirm(null);
              }}
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-orange-600 text-white hover:bg-orange-700"
              disabled={purgingSchedules}
              onClick={(e) => {
                // La purge est asynchrone : on garde le dialog ouvert (spinner)
                // jusqu'à sa fin — prepareSchedulesRun le ferme lui-même.
                e.preventDefault();
                schedulesConfirm?.resolve(true);
              }}
            >
              {purgingSchedules ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Suppression…
                </>
              ) : (
                'Remplacer les créneaux'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation de bypass d'un lot incomplet */}
      <AlertDialog
        open={!!bypassConfirm}
        onOpenChange={(open) => {
          if (!open && !bypassing) setBypassConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <FastForward className="h-5 w-5 text-amber-500" />
              Bypasser le lot « {bypassConfirm?.stepDefinition.name} » ?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  Le lot sera marqué <span className="font-semibold text-amber-600">Bypassé</span>{' '}
                  et les lots qui en dépendent seront <span className="font-semibold">débloqués</span>,
                  même s'il est incomplet
                  {(bypassConfirm?.failedRecords ?? 0) > 0 && (
                    <>
                      {' '}
                      (<span className="font-semibold text-red-600">
                        {bypassConfirm?.failedRecords} enregistrement
                        {(bypassConfirm?.failedRecords ?? 0) > 1 ? 's' : ''} en erreur
                      </span>)
                    </>
                  )}
                  .
                </p>
                <p>
                  Les lots suivants migreront alors sans les références manquantes de ce lot
                  (elles seront simplement omises).
                </p>
                <p>
                  Vous pourrez revenir sur ce lot à tout moment pour le relancer — la reprise est
                  idempotente : les enregistrements déjà migrés sont conservés, seuls les manquants sont
                  retraités.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bypassing} onClick={() => setBypassConfirm(null)}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 text-white hover:bg-amber-700"
              disabled={bypassing}
              onClick={(e) => {
                e.preventDefault();
                void confirmBypassStep();
              }}
            >
              {bypassing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Bypass…
                </>
              ) : (
                'Bypasser le lot'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Verrouillage facturation V1 — état réel des interrupteurs, puis
          verrouillage (ou rétablissement) sur confirmation. Seule action de
          l'outil qui écrit dans la base V1 (exception assumée). */}
      <AlertDialog open={showLockdown} onOpenChange={(o) => !lockdownBusy && setShowLockdown(o)}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
              <Lock className="h-5 w-5" />
              Fermer la facturation V1
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  La facturation démarre en V2 : la V1 doit cesser d'émettre des factures, sinon sa
                  numérotation avance en parallèle de celle de la V2 (doublons de numéros légaux).
                  Cette action coupe la génération automatique, la création et la validation
                  manuelles de factures et d'avoirs, les paiements en ligne des parents, les
                  remises SEPA et les étapes de paiement des inscriptions en ligne —{' '}
                  <strong>sans casser les inscriptions</strong> ni la consultation des factures
                  existantes. Réversible : les réglages d'origine sont conservés.
                </p>

                {lockdownBusy && !lockdownReport && (
                  <p className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Lecture des réglages V1…
                  </p>
                )}

                {lockdownReport && (
                  <>
                    <p
                      className={cn(
                        'rounded-md border p-3',
                        lockdownReport.switches.every((sw) => sw.locked)
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300'
                          : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
                      )}
                    >
                      {lockdownReport.switches.every((sw) => sw.locked) ? (
                        <>La facturation V1 est <strong>entièrement verrouillée</strong>.</>
                      ) : (
                        <>
                          <strong>
                            {lockdownReport.switches.filter((sw) => !sw.locked).length}
                          </strong>{' '}
                          interrupteur(s) encore ouvert(s) sur {lockdownReport.switches.length}.
                          {lockdownReport.dryRun && " Rien n'a encore été modifié."}
                        </>
                      )}
                      {lockdownReport.appliedAt && (
                        <>
                          {' '}
                          Verrouillé le{' '}
                          {new Date(lockdownReport.appliedAt).toLocaleString('fr-FR')} (réglages
                          d'origine conservés).
                        </>
                      )}
                    </p>

                    <ul className="max-h-52 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2 text-xs dark:border-slate-800">
                      {lockdownReport.switches.map((sw) => (
                        <li key={sw.key} className="flex items-center justify-between gap-2">
                          <span className="truncate">{sw.label}</span>
                          <span
                            className={cn(
                              'shrink-0',
                              sw.locked
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : 'text-amber-600 dark:text-amber-400'
                            )}
                          >
                            {sw.locked ? 'verrouillé' : `ouvert (${sw.current})`}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {lockdownReport.changes && lockdownReport.changes.length > 0 && (
                      <ul className="max-h-32 space-y-1 overflow-y-auto rounded-md border border-emerald-200 bg-emerald-50/50 p-2 text-xs text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-300">
                        {lockdownReport.changes.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    )}

                    <details className="text-xs text-slate-600 dark:text-slate-400">
                      <summary className="cursor-pointer font-medium">
                        Limites connues ({lockdownReport.warnings.length})
                      </summary>
                      <ul className="mt-1 list-disc space-y-1 pl-4">
                        {lockdownReport.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </details>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {lockdownReport?.applied && (
              <Button
                variant="outline"
                size="sm"
                disabled={lockdownBusy}
                onClick={() => void restoreLockdown()}
                title="Remettre les réglages V1 tels qu'ils étaient avant le verrouillage"
              >
                Rétablir la facturation V1
              </Button>
            )}
            <AlertDialogCancel disabled={lockdownBusy}>Fermer</AlertDialogCancel>
            {lockdownReport && !lockdownReport.switches.every((sw) => sw.locked) && (
              <AlertDialogAction
                className="bg-red-600 text-white hover:bg-red-700"
                disabled={lockdownBusy}
                onClick={(e) => {
                  e.preventDefault();
                  void applyLockdown();
                }}
              >
                {lockdownBusy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verrouillage…
                  </>
                ) : (
                  'Verrouiller la facturation V1'
                )}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Réparation des parents fusionnés — simulation, puis application */}
      <AlertDialog open={showParentRepair} onOpenChange={(o) => !parentRepairBusy && setShowParentRepair(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
              <Users className="h-5 w-5" />
              Réparer les fiches parents
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  Quand le père et la mère partagent une même adresse e-mail en V1, la nouvelle
                  plateforme ne peut pas créer deux personnes : la mère a été rattachée à la fiche du
                  père, qui se retrouve porteuse des relations « Père » <em>et</em> « Mère ».
                </p>

                {parentRepairBusy && !parentRepairReport && (
                  <p className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Analyse des familles en cours…
                  </p>
                )}

                {parentRepairReport && parentRepairReport.families === 0 && parentRepairReport.invertedRoles === 0 && (
                  <p className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
                    Aucune famille fusionnée : cette école n'est pas concernée.
                  </p>
                )}

                {parentRepairReport && (parentRepairReport.families > 0 || parentRepairReport.invertedRoles > 0) && (
                  <>
                    <p
                      className={cn(
                        'rounded-md border p-3',
                        parentRepairReport.dryRun
                          ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
                          : 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300'
                      )}
                    >
                      {parentRepairReport.dryRun ? (
                        <>
                          <strong>{parentRepairReport.families}</strong> famille(s) fusionnée(s) à
                          séparer, <strong>{parentRepairReport.relationsMoved}</strong> relation(s) à
                          déplacer, et <strong>{parentRepairReport.invertedRoles}</strong> rôle(s)
                          père/mère inversé(s) à rectifier. Rien n'a encore été modifié.
                        </>
                      ) : (
                        <>
                          <strong>{parentRepairReport.repaired}</strong> famille(s) séparée(s),{' '}
                          <strong>{parentRepairReport.relationsMoved}</strong> relation(s) déplacée(s),{' '}
                          <strong>{parentRepairReport.invertedRoles}</strong> rôle(s) rectifié(s).
                        </>
                      )}
                    </p>

                    <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2 text-xs dark:border-slate-800">
                      {parentRepairReport.details.map((d) => (
                        <li key={d.parentsId} className="flex items-center justify-between gap-2">
                          <span className="truncate">{d.mother || `Famille ${d.parentsId}`}</span>
                          <span
                            className={cn(
                              'shrink-0',
                              d.status === 'failed'
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-slate-500 dark:text-slate-400'
                            )}
                          >
                            {d.status === 'failed' ? d.error : `${d.relationsMoved} relation(s)`}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {parentRepairReport.dryRun && (
                      <p className="text-slate-600 dark:text-slate-400">
                        Les rôles inversés sont rectifiés d'après la civilité V1 ; sans civilité
                        renseignée, ils restent en l'état.{' '}

                        La mère sera recréée <strong>sans e-mail</strong> : l'adresse reste au père,
                        la plateforme n'acceptant qu'un titulaire par adresse.
                      </p>
                    )}
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={parentRepairBusy}>
              {parentRepairReport && !parentRepairReport.dryRun ? 'Fermer' : 'Annuler'}
            </AlertDialogCancel>
            {parentRepairReport && parentRepairReport.dryRun &&
              (parentRepairReport.families > 0 || parentRepairReport.invertedRoles > 0) && (
              <AlertDialogAction
                onClick={(e) => {
                  // Le dialogue doit rester ouvert pour afficher le rapport final.
                  e.preventDefault();
                  void applyParentRepair();
                }}
                disabled={parentRepairBusy}
              >
                {parentRepairBusy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Réparation…
                  </>
                ) : (
                  'Réparer'
                )}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Fiches créées deux fois par une tranche de migration interrompue */}
      <AlertDialog
        open={showDuplicateRepair}
        onOpenChange={(o) => !duplicateRepairBusy && setShowDuplicateRepair(o)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
              <CopyX className="h-5 w-5" />
              Fiches en double
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  Quand une tranche de migration est interrompue après avoir créé les personnes
                  dans la nouvelle plateforme mais avant d'avoir enregistré ce qu'elle venait de
                  faire, la reprise les recrée : l'école voit chaque personne deux fois, l'une
                  avec sa classe, l'autre sans.
                </p>

                {duplicateRepairBusy && !duplicateRepairReport && (
                  <p className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Analyse des fiches en cours…
                  </p>
                )}

                {duplicateRepairReport && duplicateRepairReport.orphans === 0 && (
                  <p className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
                    Aucune fiche en double : cette école n'est pas concernée.
                  </p>
                )}

                {duplicateRepairReport && duplicateRepairReport.orphans > 0 && (
                  <>
                    <p
                      className={cn(
                        'rounded-md border p-3',
                        duplicateRepairReport.dryRun
                          ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
                          : 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300'
                      )}
                    >
                      {duplicateRepairReport.dryRun ? (
                        <>
                          <strong>{duplicateRepairReport.orphans}</strong> fiche(s) en double
                          détectée(s), dont{' '}
                          <strong>
                            {duplicateRepairReport.orphans - duplicateRepairReport.skipped}
                          </strong>{' '}
                          à supprimer et <strong>{duplicateRepairReport.skipped}</strong> à
                          conserver. Rien n'a encore été supprimé.
                        </>
                      ) : (
                        <>
                          <strong>{duplicateRepairReport.deleted}</strong> fiche(s) supprimée(s),{' '}
                          <strong>{duplicateRepairReport.skipped}</strong> conservée(s)
                          {duplicateRepairReport.failed > 0 && (
                            <>
                              , <strong>{duplicateRepairReport.failed}</strong> en échec
                            </>
                          )}
                          .
                        </>
                      )}
                    </p>

                    <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2 text-xs dark:border-slate-800">
                      {duplicateRepairReport.details.map((d) => (
                        <li key={d.schoolUserId} className="flex items-center justify-between gap-2">
                          <span className="truncate" title={d.reason}>
                            {d.name}
                          </span>
                          <span
                            className={cn(
                              'shrink-0',
                              d.status === 'failed'
                                ? 'text-red-600 dark:text-red-400'
                                : d.status === 'no_twin' || d.status === 'has_data'
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-slate-500 dark:text-slate-400'
                            )}
                          >
                            {d.status === 'failed'
                              ? d.reason
                              : d.status === 'no_twin'
                                ? 'conservée (seule fiche)'
                                : d.status === 'has_data'
                                  ? 'conservée (données saisies)'
                                  : d.status === 'deleted'
                                    ? 'supprimée'
                                    : 'à supprimer'}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {duplicateRepairReport.dryRun && (
                      <p className="text-slate-600 dark:text-slate-400">
                        Seules les fiches ayant un homonyme conservé <em>et</em> aucune donnée
                        saisie sont supprimées. Leurs liens de parenté sont retirés au passage —
                        la fiche conservée porte déjà les siens.
                      </p>
                    )}
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={duplicateRepairBusy}>
              {duplicateRepairReport && !duplicateRepairReport.dryRun ? 'Fermer' : 'Annuler'}
            </AlertDialogCancel>
            {duplicateRepairReport &&
              duplicateRepairReport.dryRun &&
              duplicateRepairReport.orphans - duplicateRepairReport.skipped > 0 && (
                <AlertDialogAction
                  onClick={(e) => {
                    // Le dialogue doit rester ouvert pour afficher le rapport final.
                    e.preventDefault();
                    void applyDuplicateRepair();
                  }}
                  disabled={duplicateRepairBusy}
                >
                  {duplicateRepairBusy ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Suppression…
                    </>
                  ) : (
                    'Supprimer les doublons'
                  )}
                </AlertDialogAction>
              )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* « Recommencer » — choix du mode (recréer l'env V2 ou dossier seul) */}
      <AlertDialog open={showRestartConfirm} onOpenChange={(o) => !restarting && setShowRestartConfirm(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
              <RotateCcw className="h-5 w-5" />
              Recommencer la migration
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Choisissez ce que vous souhaitez réinitialiser :</p>

                {/* Option 1 : recréer tout l'environnement V2 (→ wizard) */}
                <button
                  type="button"
                  onClick={() => setRestartMode('full')}
                  disabled={restarting}
                  className={cn(
                    'w-full rounded-lg border p-3 text-left transition disabled:opacity-60',
                    restartMode === 'full'
                      ? 'border-red-400 bg-red-50 ring-1 ring-red-400 dark:border-red-800 dark:bg-red-950/40'
                      : 'border-gray-200 hover:border-gray-300 dark:border-slate-700 dark:hover:border-slate-600'
                  )}
                >
                  <div className="flex items-start gap-2.5">
                    <RotateCcw className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                    <div className="space-y-0.5">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        Recréer tout l'environnement V2
                      </p>
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        Purge le dossier de migration et supprime l'environnement V2 (tenant + utilisateur de base).
                        Vous êtes ensuite ramené au <span className="font-medium">workflow de création V2</span> pour
                        tout reconfigurer (offre, branding, établissements). <span className="font-medium">Pas de
                        redéploiement automatique.</span>
                      </p>
                    </div>
                  </div>
                </button>

                {/* Option 2 : recommencer le dossier de migration (→ redéploiement auto) */}
                <button
                  type="button"
                  onClick={() => setRestartMode('migration_only')}
                  disabled={restarting}
                  className={cn(
                    'w-full rounded-lg border p-3 text-left transition disabled:opacity-60',
                    restartMode === 'migration_only'
                      ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-400 dark:border-amber-800 dark:bg-amber-950/40'
                      : 'border-gray-200 hover:border-gray-300 dark:border-slate-700 dark:hover:border-slate-600'
                  )}
                >
                  <div className="flex items-start gap-2.5">
                    <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <div className="space-y-0.5">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        Recommencer uniquement le dossier de migration
                      </p>
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        Purge le dossier de migration et supprime le tenant V2 (+ utilisateur de base), puis{' '}
                        <span className="font-medium">redéploie automatiquement</span> avec la même configuration
                        (offre, modules) — le dialog de progression s'ouvre seul.
                      </p>
                    </div>
                  </div>
                </button>

                <p className="text-slate-600 dark:text-slate-400">
                  Les mappings référentiels (matière/niveau) et décisions manuelles de l'école sont{' '}
                  <span className="font-medium">conservés</span> dans les deux cas (réutilisés à la prochaine migration).
                </p>

                {/* Garde-fou : les deux modes suppriment le tenant V2 (irréversible) */}
                <p>
                  Cette action est{' '}
                  <span className="font-semibold text-red-600">destructrice et irréversible</span>. Pour confirmer,
                  saisissez le code de l'école{' '}
                  <span className="font-mono font-semibold text-slate-900 dark:text-slate-100">{school.code}</span> :
                </p>
                <input
                  type="text"
                  value={restartConfirmText}
                  onChange={(e) => setRestartConfirmText(e.target.value)}
                  disabled={restarting}
                  autoComplete="off"
                  placeholder={school.code}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-900 focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restarting} onClick={() => setShowRestartConfirm(false)}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(
                'text-white',
                restartMode === 'full'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-amber-600 hover:bg-amber-700'
              )}
              disabled={restarting || restartConfirmText.trim() !== school.code}
              onClick={(e) => {
                e.preventDefault();
                void handleRestartFromScratch();
              }}
            >
              {restarting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Réinitialisation…
                </>
              ) : restartMode === 'full' ? (
                "Supprimer et recréer l'environnement"
              ) : (
                'Réinitialiser et redéployer'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Config dialog */}
      <ProjectConfigDialog
        open={showConfigDialog}
        project={project}
        onClose={() => setShowConfigDialog(false)}
        onSaved={setProject}
      />

      {/* Role mappings drawer (déclenché depuis le groupe Utilisateurs) */}
      <RoleMappingsDialog
        open={showRoleMappings}
        schoolId={school.id}
        schoolName={school.name}
        onClose={() => {
          setShowRoleMappings(false);
          // Rafraîchit le chip de complétude des rôles après édition.
          setMappingReloadTick((t) => t + 1);
        }}
      />

      {/* Drawer mapping par lot — dispatcher selon le type configuré dans
          le registre. `custom_field` ouvre un drawer dédié (enum V2 statique).
          Les autres types (cycle, niveau) utilisent LotMappingDialog. */}
      {mappingLot && LOT_MAPPING_REGISTRY[mappingLot.stepDefinition.code]?.type === 'custom_field' ? (
        <CustomFieldsMappingDialog
          open={!!mappingLot}
          schoolId={school.id}
          schoolName={school.name}
          lotCode={mappingLot.stepDefinition.code}
          lotLabel={mappingLot.stepDefinition.name}
          onClose={() => {
            setMappingLot(null);
            setMappingReloadTick((t) => t + 1);
          }}
          onSaved={() => setMappingReloadTick((t) => t + 1)}
        />
      ) : mappingLot ? (
        <LotMappingDialog
          open={!!mappingLot}
          schoolId={school.id}
          schoolName={school.name}
          lotCode={mappingLot.stepDefinition.code}
          lotLabel={mappingLot.stepDefinition.name}
          isEducationNationale={school.isEducationNationale}
          onClose={() => {
            setMappingLot(null);
            // Recharge l'état du mapping : le badge complet/partiel/aucun et le
            // déblocage de la migration reflètent les associations qui viennent
            // d'être éditées dans le drawer.
            setMappingReloadTick((t) => t + 1);
          }}
        />
      ) : null}

    </div>
  );
}
